import { Injectable } from '@angular/core';

export const INVOICE_V2_LOCAL_DRAFT_VERSION = 1;

export interface InvoiceV2LocalDraftUiState {
  hasItemsForSelectedType: boolean;
  paymentMethodsRequired: boolean;
}

/** Human-readable snapshot for the restore dialog (stored at save time). */
export interface InvoiceV2LocalDraftSummary {
  companyName: string;
  customerName: string;
  invoiceTypeLabel: string;
  invoiceDateIso: string | null;
  itemCount: number;
  paymentMethodCount: number;
  totalAmount: number | null;
  hasNote: boolean;
}

export interface InvoiceV2LocalDraft {
  version: number;
  savedAt: string;
  merchantId: number;
  userId: number;
  formValue: Record<string, unknown>;
  uiState: InvoiceV2LocalDraftUiState;
  summary?: InvoiceV2LocalDraftSummary;
}

export interface InvoiceV2DraftRestoreDialogLabels {
  lead: string;
  savedAtLabel: string;
  companyLabel: string;
  customerLabel: string;
  documentTypeLabel: string;
  documentDateLabel: string;
  itemsLabel: string;
  paymentsLabel: string;
  totalLabel: string;
  noteLabel: string;
  notePresent: string;
  footer: string;
  notSet: string;
}

export interface InvoiceV2DraftRestoreDialogOptions {
  savedAt: string;
  summary: InvoiceV2LocalDraftSummary;
  labels: InvoiceV2DraftRestoreDialogLabels;
  isRtl: boolean;
  locale: string;
}

@Injectable({
  providedIn: 'root',
})
export class InvoiceV2LocalDraftService {
  private readonly storageKeyPrefix = 'invoice-v2-local-draft';

  getStorageKey(merchantId: number, userId: number): string {
    return `${this.storageKeyPrefix}:${merchantId}:${userId}`;
  }

  save(
    merchantId: number,
    userId: number,
    formValue: Record<string, unknown>,
    uiState: InvoiceV2LocalDraftUiState,
    summary?: InvoiceV2LocalDraftSummary,
  ): void {
    if (!merchantId || !userId) {
      return;
    }

    const draft: InvoiceV2LocalDraft = {
      version: INVOICE_V2_LOCAL_DRAFT_VERSION,
      savedAt: new Date().toISOString(),
      merchantId,
      userId,
      formValue: this.serializeFormValue(formValue),
      uiState,
      summary,
    };

    try {
      localStorage.setItem(this.getStorageKey(merchantId, userId), JSON.stringify(draft));
    } catch {
      // Quota exceeded or private mode — ignore silently
    }
  }

  load(merchantId: number, userId: number): InvoiceV2LocalDraft | null {
    if (!merchantId || !userId) {
      return null;
    }

    try {
      const raw = localStorage.getItem(this.getStorageKey(merchantId, userId));
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as InvoiceV2LocalDraft;
      if (!parsed || parsed.version !== INVOICE_V2_LOCAL_DRAFT_VERSION) {
        this.clear(merchantId, userId);
        return null;
      }

      if (+parsed.merchantId !== +merchantId || +parsed.userId !== +userId) {
        return null;
      }

      return parsed;
    } catch {
      this.clear(merchantId, userId);
      return null;
    }
  }

  clear(merchantId: number, userId: number): void {
    if (!merchantId || !userId) {
      return;
    }

    try {
      localStorage.removeItem(this.getStorageKey(merchantId, userId));
    } catch {
      // ignore
    }
  }

  hasDraft(merchantId: number, userId: number): boolean {
    return this.load(merchantId, userId) != null;
  }

  /** True when the user entered something worth restoring. */
  isFormValueMeaningful(formValue: Record<string, unknown> | null | undefined): boolean {
    if (!formValue) {
      return false;
    }

    if (formValue.customer_id != null && formValue.customer_id !== '') {
      return true;
    }

    const note = String(formValue.invoice_note ?? '').trim();
    if (note.length > 0) {
      return true;
    }

    const items = formValue.items;
    if (Array.isArray(items) && items.length > 0) {
      return true;
    }

    const payments = formValue.payment_methods;
    if (Array.isArray(payments) && payments.length > 0) {
      return true;
    }

    return false;
  }

  serializeFormValue(formValue: Record<string, unknown>): Record<string, unknown> {
    const clone = JSON.parse(JSON.stringify(formValue)) as Record<string, unknown>;

    if (clone.invoice_date) {
      clone.invoice_date = this.dateToIso(clone.invoice_date);
    }
    if (clone.due_date) {
      clone.due_date = this.dateToIso(clone.due_date);
    }

    if (Array.isArray(clone.payment_methods)) {
      clone.payment_methods = (clone.payment_methods as Record<string, unknown>[]).map((pm) => {
        const next = { ...pm };
        const details = next.details as Record<string, unknown> | undefined;
        if (details?.date) {
          next.details = { ...details, date: this.dateToIso(details.date) };
        }
        return next;
      });
    }

    return clone;
  }

  deserializeFormValue(stored: Record<string, unknown>): Record<string, unknown> {
    const clone = JSON.parse(JSON.stringify(stored)) as Record<string, unknown>;

    if (clone.invoice_date) {
      clone.invoice_date = this.isoToDate(clone.invoice_date);
    }
    if (clone.due_date) {
      clone.due_date = this.isoToDate(clone.due_date);
    }

    if (Array.isArray(clone.payment_methods)) {
      clone.payment_methods = (clone.payment_methods as Record<string, unknown>[]).map((pm) => {
        const next = { ...pm };
        const details = next.details as Record<string, unknown> | undefined;
        if (details?.date) {
          next.details = { ...details, date: this.isoToDate(details.date) };
        }
        return next;
      });
    }

    clone.confirm = false;
    return clone;
  }

  formatSavedAt(savedAt: string, locale: string): string {
    const date = new Date(savedAt);
    if (Number.isNaN(date.getTime())) {
      return savedAt;
    }

    const lang = locale === 'ar' ? 'ar' : locale === 'he' ? 'he-IL' : 'en-GB';
    try {
      return new Intl.DateTimeFormat(lang, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
    } catch {
      return date.toLocaleString();
    }
  }

  formatDraftDate(iso: string | null | undefined, locale: string): string {
    if (!iso) {
      return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const lang = locale === 'ar' ? 'ar' : locale === 'he' ? 'he-IL' : 'en-GB';
    try {
      return new Intl.DateTimeFormat(lang, { dateStyle: 'medium' }).format(date);
    } catch {
      return date.toLocaleDateString();
    }
  }

  formatAmount(amount: number | null | undefined, locale: string): string {
    if (amount == null || !Number.isFinite(amount)) {
      return '';
    }
    const lang = locale === 'ar' ? 'ar' : locale === 'he' ? 'he-IL' : 'en-GB';
    try {
      return `${new Intl.NumberFormat(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ₪`;
    } catch {
      return `${amount.toFixed(2)} ₪`;
    }
  }

  toIsoDate(value: unknown): string | null {
    return this.dateToIso(value);
  }

  buildRestoreDialogHtml(options: InvoiceV2DraftRestoreDialogOptions): string {
    const { savedAt, summary, labels, isRtl, locale } = options;
    const dir = isRtl ? 'rtl' : 'ltr';
    const align = isRtl ? 'right' : 'left';

    const row = (label: string, value: string, emphasize = false) => `
      <div class="iv2-draft-row" style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid #eceff3;">
        <span style="color:#6b7280;font-size:13px;flex-shrink:0;">${this.escapeHtml(label)}</span>
        <span style="color:#111827;font-size:14px;font-weight:${emphasize ? '600' : '500'};text-align:${align};word-break:break-word;">${this.escapeHtml(value)}</span>
      </div>`;

    const rows: string[] = [];

    const company = summary.companyName?.trim();
    if (company) {
      rows.push(row(labels.companyLabel, company));
    }

    const customer = summary.customerName?.trim();
    if (customer) {
      rows.push(row(labels.customerLabel, customer));
    }

    const docType = summary.invoiceTypeLabel?.trim();
    if (docType) {
      rows.push(row(labels.documentTypeLabel, docType));
    }

    const hasMeaningfulProgress =
      summary.itemCount > 0 ||
      !!customer ||
      summary.hasNote ||
      summary.paymentMethodCount > 0 ||
      (summary.totalAmount != null && summary.totalAmount > 0);

    const docDate = this.formatDraftDate(summary.invoiceDateIso, locale);
    if (docDate && hasMeaningfulProgress) {
      rows.push(row(labels.documentDateLabel, docDate));
    }

    if (summary.itemCount > 0) {
      rows.push(row(labels.itemsLabel, `${summary.itemCount}`));
    }

    if (summary.paymentMethodCount > 0) {
      rows.push(row(labels.paymentsLabel, `${summary.paymentMethodCount}`));
    }

    if (summary.totalAmount != null && summary.totalAmount > 0) {
      rows.push(row(labels.totalLabel, this.formatAmount(summary.totalAmount, locale), true));
    }

    if (summary.hasNote) {
      rows.push(row(labels.noteLabel, labels.notePresent));
    }

    const detailsBlock =
      rows.length > 0
        ? `<div style="margin-top:16px;padding:4px 16px 8px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;">${rows.join('')}</div>`
        : '';

    return `
      <div class="iv2-draft-restore" dir="${dir}" style="text-align:${align};font-family:inherit;">
        <p style="margin:0 0 4px;color:#4b5563;font-size:14px;line-height:1.55;">${this.escapeHtml(labels.lead)}</p>
        <div style="display:inline-flex;align-items:center;gap:8px;margin-top:12px;padding:6px 12px;background:#fef3f2;border:1px solid #fecaca;border-radius:999px;color:#b42318;font-size:12px;font-weight:600;">
          <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#e62e2d;"></span>
          ${this.escapeHtml(labels.savedAtLabel)}: ${this.escapeHtml(savedAt)}
        </div>
        ${detailsBlock}
        <p style="margin:14px 0 0;color:#9ca3af;font-size:12px;line-height:1.5;">${this.escapeHtml(labels.footer)}</p>
      </div>`;
  }

  private escapeHtml(value: string): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private dateToIso(value: unknown): string | null {
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
    }
    return null;
  }

  private isoToDate(value: unknown): Date | null {
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }
}
